import { useState, useMemo } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";

import { cn } from "../../helpers/cn";

import {
  useGetOrderVolumeNotification,
  volumeTimePeriodOptions,
  volumeShipmentMetricOptions,
  formatVolumeData,
} from "./clientDashboard.utils";

function Container({ children }) {
  return <div className="paper-panel tw-h-full">{children}</div>;
}

function VolumeLineChart() {
  const {
    data: response,
    isLoading,
    isError,
    isSuccess,
  } = useGetOrderVolumeNotification();

  const [volumeTimePeriod, setVolumeTimePeriod] = useState({
    label: "1 Year",
    value: "1Y",
    momentUnit: "years",
    momentUnitCount: 1,
  });

  const [volumeShipmentMetric, setVolumeShipmentMetric] = useState({
    label: "Total Volume",
    keyValue: "totalVolume",
    description:
      "Note: Total Volume is the sum of all volume types (engines, transmissions, oversized, parcels, pallets, pieces)",
  });
  const volumes = useMemo(
    () => formatVolumeData(response?.data, volumeTimePeriod),
    [response?.data, volumeTimePeriod]
  );

  if (isLoading)
    return (
      <Container>
        <div className="tw-flex tw-justify-center tw-items-center tw-h-full">
          <div className="spinner-border text-primary" role="status" />
        </div>
      </Container>
    );

  if (isError)
    return (
      <Container>
        <div className="tw-flex tw-justify-center tw-items-center tw-h-full">
          <div className="text-danger font-semibold">Error fetching data</div>
        </div>
        ;
      </Container>
    );

  return (
    <Container>
      <div className="tw-pt-6 tw-pb-8 tw-px-4 md:tw-px-8 tw-h-full tw-flex tw-flex-col">
        <div className="tw-flex tw-gap-x-6 tw-mb-6">
          <select
            className="form-control tw-w-auto"
            id="volumeShipmentMetric"
            value={volumeShipmentMetric.keyValue}
            onChange={(event) => {
              const selectedOption = volumeShipmentMetricOptions.find(
                (option) => option.keyValue === event.target.value
              );

              setVolumeShipmentMetric(selectedOption);
            }}
          >
            {volumeShipmentMetricOptions.map((option) => (
              <option key={option.keyValue} value={option.keyValue}>
                {option.label}
              </option>
            ))}
          </select>

          <select
            className="form-control tw-w-auto"
            id="volumeTimePeriod"
            value={volumeTimePeriod.value}
            onChange={(event) => {
              const selectedOption = volumeTimePeriodOptions.find(
                (option) => option.value === event.target.value
              );

              setVolumeTimePeriod(selectedOption);
            }}
          >
            {volumeTimePeriodOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="tw-flex-grow">
          <ResponsiveContainer
            className={cn(
              `${
                isSuccess && volumeShipmentMetric.description
                  ? "tw-max-h-80"
                  : " tw-max-h-76"
              }`
            )}
          >
            <LineChart
              width={500}
              data={volumes}
              margin={{ bottom: -5, left: -20, right: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="dateLabel" />
              <YAxis />
              <Tooltip />

              <Line
                type="monotone"
                dataKey={volumeShipmentMetric?.keyValue}
                name={volumeShipmentMetric?.label}
                stroke="#007bff"
                strokeWidth={1.5}
                dot={{
                  strokeWidth: 2,
                }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {isSuccess && volumeShipmentMetric?.description && (
          <div className="tw-text-base tw-text-gray-500 tw-mt-2 tw-text-center">
            {volumeShipmentMetric.description}
          </div>
        )}
      </div>
    </Container>
  );
}

export default VolumeLineChart;
